/**
 * Animated life total for the player HUD. Damage doesn't snap: the
 * number ticks down one point at a time on a bell-curve cadence. Slow
 * first ticks, accelerating through the middle, easing out at the end.
 * so a big hit reads as a wind-up rather than a jump cut. Every
 * downward tick pops the digit with a red aura and plays the same tick
 * used when hovering a card in hand. Heals count up briskly with no
 * drama. A new hit landing mid-animation simply retargets from the
 * currently displayed value.
 */
import { useState, useEffect, useRef } from 'preact/hooks';
import { UI, playUI } from '../../utils/arena/uiSounds';

// Per-tick delay across the run: slow at the edges, fast in the middle.
// (1+cos(2πp))/2 is 1 at p=0 and p=1, 0 at p=0.5. Long runs compress so
// a 15+ point hit doesn't take half a minute.
function tickDelay(i, n) {
  const p = n === 1 ? 0.5 : i / (n - 1);
  const big = n > 12;
  const minD = big ? 32 : 50;
  const maxD = big ? 115 : 175;
  return minD + (maxD - minD) * ((1 + Math.cos(2 * Math.PI * p)) / 2);
}

export default function HudLifeTicker({ value, className, onDamage, inFight = false, release = 0 }) {
  const [display, setDisplay] = useState(value);
  const [pulse, setPulse] = useState(0);
  const timers = useRef([]);
  const displayRef = useRef(value);
  displayRef.current = display;
  const lastReleaseRef = useRef(release);

  useEffect(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    const from = displayRef.current;
    const released = release !== lastReleaseRef.current;
    lastReleaseRef.current = release;
    if (value === from) return;

    if (value > from) {
      // Heal: quick, quiet count-up.
      const up = () => {
        setDisplay((d) => {
          const next = Math.min(value, d + 1);
          if (next < value) timers.current.push(setTimeout(up, 50));
          return next;
        });
      };
      timers.current.push(setTimeout(up, 50));
      return;
    }

    // Combat life loss applies at resolution, seconds before the combat
    // overlay stages the hit. Hold the countdown until the overlay
    // releases it on the impact frame (release timestamp bump), or
    // until the fight clears entirely (fallback so a skipped/stale
    // playback can never wedge the number).
    if (inFight && !released) return;

    const n = from - value;
    onDamage?.(n);
    let t = 0;
    for (let i = 0; i < n; i++) {
      t += tickDelay(i, n);
      timers.current.push(setTimeout(() => {
        setDisplay((d) => d - 1);
        setPulse((x) => x + 1);
        try { playUI(UI.HOVER, { volume: 0.35 }); } catch { /* sound is best-effort */ }
      }, t));
    }
  }, [value, inFight, release]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const ticking = display > value;
  return (
    <span className={className} style={ticking ? { color: '#ff6b61' } : undefined}>
      {/* Keyed leaf span: each tick remounts it so the pop-and-glow
          animation replays from frame zero. */}
      <span
        key={pulse}
        className={pulse ? 'vh-life-tick-pop' : undefined}
        style={{ display: 'inline-block' }}
      >
        {display}
      </span>
    </span>
  );
}
